import React, { useState, useEffect } from "react";
import { Row, Col, Container, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import PhoneInput from "react-phone-number-input";
import 'react-phone-number-input/style.css';
import moment from "moment";
import "../../Pages/AddFund/AddFund.css";
import "./../Rewards/With.css";
import apiCall from "./../../API/API";

// Admin registration of a real member, optionally with a back-dated joining date.
// The member list lives on the All Users page.
const RegisterUser = () => {
  // --- STATE FOR REGISTRATION FORM ---
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [sponsor, setSponsor] = useState("");
  const today = moment().format("YYYY-MM-DD");
  const [joiningDate, setJoiningDate] = useState(today);
  const [loading, setLoading] = useState(false);
  const [typingTimeout, setTypingTimeout] = useState(null);
  const [sponsorName, setSponsorName] = useState("");
  const [username, setUsername] = useState("");
  // { state: "checking" | "ok" | "bad", message }
  const [usernameCheck, setUsernameCheck] = useState(null);

  // Is the username free? Checked shortly after typing stops.
  useEffect(() => {
    const value = username.trim();
    if (!value) {
      setUsernameCheck(null);
      return undefined;
    }
    setUsernameCheck({ state: "checking", message: "Checking…" });
    const t = setTimeout(async () => {
      const { data, error } = await apiCall("GET", `/username-available?username=${encodeURIComponent(value)}`);
      if (error) return setUsernameCheck({ state: "bad", message: error.message || "Could not check the username." });
      setUsernameCheck({ state: data?.available ? "ok" : "bad", message: data?.message || "" });
    }, 450);
    return () => clearTimeout(t);
  }, [username]);

  const token = localStorage.getItem("authToken");

  // --- FUNCTIONS FOR REGISTRATION FORM ---

  useEffect(() => {
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }
    if (sponsor) {
      setTypingTimeout(
        setTimeout(() => {
          checkSponsorExistence();
        }, 500)
      );
    } else {
      setSponsorName("");
    }
    return () => clearTimeout(typingTimeout);
  }, [sponsor]);

  const checkSponsorExistence = async () => {
    setLoading(true);
    try {
      const { data, error } = await apiCall("post", "/check_username", {
        username: sponsor,
      });
      if (data) {
        setSponsorName(data.name);
      } else if (error) {
        setSponsorName("Sponsor not found");
      }
    } catch (err) {
      toast.error("An error occurred while checking sponsor.");
      console.error("Sponsor Check Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setName("");
    setEmail("");
    setMobile("");
    setPassword("");
    setSponsor("");
    setJoiningDate(today);
    setSponsorName("");
    setUsername("");
    setUsernameCheck(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (usernameCheck?.state !== "ok") {
      toast.error(usernameCheck?.message || "Choose a username that is available.");
      return;
    }
    setLoading(true);
    const requestBody = {
      username: username.trim(),
      name,
      email,
      mobile,
      password,
      sponsor,
      joining_date: joiningDate,
    };

    try {
        const config = {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: token,
          },
        };
      const { data, error } = await apiCall(
        "post",
        "/user-Register-fromAdmin",
        requestBody,
        config
      );

      if (data) {
        toast.success(data.message || "User registered successfully!");
        handleCancel();
      } else if (error) {
        toast.error(error.message || "Failed to register user.");
      }
    } catch (err) {
      toast.error("An error occurred during registration.");
      console.error("Registration Error:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <React.Fragment>
      <Container className="mt-4">
        {/* --- SECTION 1: REGISTRATION FORM --- */}
        <section id="register-user-section">
          <div className="d-flex justify-content-center">
            <h2>Register User</h2>
          </div>
          <section id="formContent">
            <div className="form">
              <Row>
                <Col lg={6} style={{ margin: "auto" }}>
                  <form onSubmit={handleSubmit}>
                    <div id="formFields">
                      <div className="mb-3">
                        <div className="d-flex justify-content-between">
                          <label htmlFor="sponsor">Sponsor Username:</label>
                          <label style={{ textAlign: "end" }}>
                            {loading && sponsor ? "Checking..." : sponsorName}
                          </label>
                        </div>
                        <input
                          type="text"
                          id="sponsor"
                          className="form-control"
                          value={sponsor}
                          onChange={(e) => setSponsor(e.target.value)}
                          placeholder="Enter sponsor's username"
                          required
                        />
                      </div>
                      <div className="mb-3">
                        <div className="d-flex justify-content-between">
                          <label htmlFor="username">Username (for login):</label>
                          {usernameCheck && (
                            <label
                              style={{
                                textAlign: "end",
                                color: usernameCheck.state === "ok" ? "#15803d" : usernameCheck.state === "bad" ? "#b91c1c" : undefined,
                              }}
                            >
                              {usernameCheck.message}
                            </label>
                          )}
                        </div>
                        <input
                          type="text"
                          id="username"
                          className="form-control"
                          value={username}
                          onChange={(e) => setUsername(e.target.value.replace(/\s/g, ""))}
                          placeholder="e.g. AD1234567"
                          maxLength={20}
                          autoComplete="off"
                          required
                        />
                        <small className="text-muted">4–20 letters or digits. It must not be used by anyone else.</small>
                      </div>
                      <div className="mb-3">
                        <label htmlFor="name">User Full Name:</label>
                        <input
                          type="text"
                          id="name"
                          className="form-control"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="Enter User's full name"
                          required
                        />
                      </div>
                      <div className="mb-3">
                        <label htmlFor="email">Email:</label>
                        <input
                          type="email"
                          id="email"
                          className="form-control"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="Enter User's email"
                          required
                        />
                      </div>
                      <div className="mb-3">
                        <label htmlFor="phoneInput">Contact Number:</label>
                        <PhoneInput
                          id="phoneInput"
                          international
                          countryCallingCodeEditable={false}
                          defaultCountry="IN"
                          value={mobile}
                          onChange={setMobile}
                          placeholder="Enter user's mobile number"
                        />
                      </div>
                      <div className="mb-3">
                        <label htmlFor="joiningDate">Joining Date:</label>
                        <input
                          type="date"
                          id="joiningDate"
                          className="form-control"
                          value={joiningDate}
                          max={today}
                          onChange={(e) => setJoiningDate(e.target.value)}
                          required
                        />
                      </div>
                      <div className="mb-3">
                        <label htmlFor="password">Password:</label>
                        <input
                          type="password"
                          id="password"
                          className="form-control"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Set user's password"
                          required
                        />
                      </div>
                      <div className="d-flex justify-content-between">
                        <Button variant="secondary" className="me-2" onClick={handleCancel}>
                          Cancel
                        </Button>
                        <Button type="submit" variant="primary" disabled={loading || usernameCheck?.state !== "ok"}>
                          {loading ? "Registering..." : "Register User"}
                        </Button>
                      </div>
                    </div>
                  </form>
                </Col>
              </Row>
            </div>
          </section>
        </section>

      </Container>
    </React.Fragment>
  );
};

export default RegisterUser;
